import React, { useContext, useEffect, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import classnames from 'classnames'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { setManageSubsOptions, setCurrentSubscriptionName } from '../../../../../actions/index'
import { SubscriptionStore } from '../subscription-plan'
import { useMutateAndLoading } from '../../../../../hooks/use-mutate-and-loading'
import { useOneWaySubscriptionChange } from '../../../../../hooks/useOneWaySubscriptionChange'
import useShowModal from '../../../../../hooks/use-show-modal'
import { segmentTrackResumeSubscription } from '../../../../../segment/segment-track'
import REACTIVATE_SUBSCRIPTION_MUTATION from '../../../../../../graphql/mutations/reactivate-subscription.gql'
import closeIcon from '../../../../../../images/closeModal.svg'
import editIcon from '../../../../../../images/pencil.svg'
import resumeIcon from '../../../../../../images/resume.svg'
import addIcon from '../../../../../../images/add-to-list.svg'

import {
  MODALS,
  EV_SUBSCRIPTION_STATUS,
  MANAGE_PLAN_OPTIONS,
  CANCEL_SUBSCRIPTION_SCREENS
} from '../../../../../constants'
import styles from '../subscription-plan.css'
import { modalContent } from '../../../../../modules/shared/subscription-constants'
import {
  isUpgradeAllowed,
  isDowngradeAllowed
} from '../../../../../modules/change-subscription'
import renderIconButton from '../../../../shared/settings/render-icon-button'

const ManageSubscription = () => {
  const history = useHistory()
  const showModal = useShowModal()
  const [reactivateSubscription] = useMutation(REACTIVATE_SUBSCRIPTION_MUTATION)
  const mutateAndLoading = useMutateAndLoading(reactivateSubscription)

  const {
    currentSubscription,
    upcomingFinalBillSubscription,
    handleUpdateSubscription,
    evSubscriptionStatus,
    evUpcomingSubscriptionStatus,
    manageSubsOptions,
    recurringVouchers
  } = useContext(SubscriptionStore)
  const [cancelSubscription, setCancelSubscription] = useState()
  const [addSubscription, setAddSubscription] = useState()
  const [updateSubscription, setUpdateSubscription] = useState()
  const [resumeSubscription, setResumeSubscription] = useState()
  const [upgradeSubscription, setUpgradeSubscription] = useState()
  const [downgradeSubscription, setDowngradeSubscription] = useState()

  const oneWayChangeDirection = useOneWaySubscriptionChange(manageSubsOptions)
  const sku = currentSubscription?.sku || ''
  const dispatch = useDispatch()

  const handleResumeSubscription = ({
    isUpcomingCancelled,
    upcomingFinalBillSku
  }) => () => {
    const reactivationSku =
      isUpcomingCancelled && upcomingFinalBillSku
        ? upcomingFinalBillSku
        : sku

    // Add segment data analytics for resume subscription
    segmentTrackResumeSubscription({ sku: reactivationSku })

    return mutateAndLoading('reactivateSubscription', {
      variables: { sku: reactivationSku }
    }).then(handleUpdateSubscription)
  }

  // Store current plan details in Redux so the plan change flow can use them
  useEffect(() => {
    dispatch(setManageSubsOptions(manageSubsOptions))
    currentSubscription && dispatch(setCurrentSubscriptionName(currentSubscription.name))
  }, [manageSubsOptions, sku])

  // Cancel
  useEffect(() => {
    if (manageSubsOptions.includes(MANAGE_PLAN_OPTIONS.CANCEL)) {
      const cancel = renderIconButton(
        () => {
          history.push(CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_LANDING.path)
        },
        'cancel',
        classnames(styles.iconButton, styles.cancelButton),
        'Cancel',
        closeIcon
      )
      setCancelSubscription(cancel)
    } else {
      setCancelSubscription(null)
    }
  }, [manageSubsOptions])

  // Add Plan
  useEffect(() => {
    if (manageSubsOptions.includes(MANAGE_PLAN_OPTIONS.ADD)) {
      const choose = renderIconButton(
        () => {
          showModal(MODALS.qsParams.addSubscription, {
            content: modalContent.selection
          })
        },
        'choose',
        styles.iconButton,
        'Add Plan',
        addIcon
      )
      setAddSubscription(choose)
    } else {
      setAddSubscription(null)
    }
  }, [manageSubsOptions])

  // Change Plan
  useEffect(() => {
    const canChangePlan =
      manageSubsOptions.includes(MANAGE_PLAN_OPTIONS.UPGRADE) ||
      manageSubsOptions.includes(MANAGE_PLAN_OPTIONS.DOWNGRADE)

    if (canChangePlan) {
      const choose = renderIconButton(
        () => {
          showModal(MODALS.qsParams.addSubscription, {
            content: modalContent.selection,
            currentSubscription: currentSubscription.name,
            oneWayChangeDirection,
            hasRecurringVouchers: !!(recurringVouchers && recurringVouchers.length)
          })
        },
        'update',
        styles.iconButton,
        'Change Plan',
        editIcon
      )
      setUpdateSubscription(choose)
    } else {
      setUpdateSubscription(null)
    }
  }, [manageSubsOptions, sku])

  // Resume Plan
  useEffect(() => {
    if (manageSubsOptions.includes(MANAGE_PLAN_OPTIONS.RESUME)) {
      const upcomingFinalBillSku = upcomingFinalBillSubscription?.sku || ''
      const isUpcomingCancelled =
        evUpcomingSubscriptionStatus === EV_SUBSCRIPTION_STATUS.FINAL_BILL
      const resume = renderIconButton(
        handleResumeSubscription({
          isUpcomingCancelled,
          upcomingFinalBillSku
        }),
        'Resume',
        styles.iconButton,
        'Resume Plan',
        resumeIcon
      )
      setResumeSubscription(resume)
    } else {
      setResumeSubscription(null)
    }
  }, [
    manageSubsOptions,
    upcomingFinalBillSubscription,
    mutateAndLoading,
    sku,
    handleUpdateSubscription,
    evSubscriptionStatus,
    evUpcomingSubscriptionStatus
  ])

  // Upgrade - Switch to Annual
  useEffect(() => {
    const upgradeAllowed = isUpgradeAllowed(
      evSubscriptionStatus,
      evUpcomingSubscriptionStatus,
      sku
    )
    if (upgradeAllowed) {
      const upgrade = renderIconButton(
        () => showModal(MODALS.qsParams.upgradeSubscription),
        'Upgrade',
        classnames(styles.iconButton, styles.changeSubscriptionButton),
        'Switch to Annual Subscription'
      )

      setUpgradeSubscription(upgrade)
    } else {
      setUpgradeSubscription(null)
    }
  }, [evSubscriptionStatus, evUpcomingSubscriptionStatus, sku])

  // Downgrade - Switch to Monthly
  useEffect(() => {
    const downgradeAllowed = isDowngradeAllowed(
      evSubscriptionStatus,
      evUpcomingSubscriptionStatus,
      sku
    )
    if (downgradeAllowed) {
      const downgrade = renderIconButton(
        () => showModal(MODALS.qsParams.downgradeSubscription),
        'Downgrade',
        classnames(styles.iconButton, styles.changeSubscriptionButton),
        'Switch to Monthly Subscription'
      )
      setDowngradeSubscription(downgrade)
    } else {
      setDowngradeSubscription(null)
    }
  }, [evSubscriptionStatus, evUpcomingSubscriptionStatus, sku])

  if (
    !addSubscription &&
    !resumeSubscription &&
    !upgradeSubscription &&
    !downgradeSubscription &&
    !cancelSubscription &&
    !updateSubscription
  ) {
    return null
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>Manage your plan</h2>
      <div className={styles.buttonsFrame}>
        {addSubscription}
        {resumeSubscription}
        {updateSubscription}
        {cancelSubscription}
      </div>
    </div>
  )
}

export default ManageSubscription
